import { h, resolveComponent } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import store from '@/store'

import DefaultLayout from '@/layouts/DefaultLayout'

const routes = [
  {
    path: '/access',
    redirect: '/access/404',
    name: 'Access',
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    children: [
      {
        path: '404',
        name: 'Access-404',
        component: () => import('@/views/access/Page404'),
      },
      {
        path: '500',
        name: 'Access-500',
        component: () => import('@/views/access/Page500'),
      },
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/access/Login'),
      },
      {
        path: 'register',
        name: 'Register',
        component: () => import('@/views/access/Register'),
      },
      {
        path: 'reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/access/ResetPassword'),
      },
      {
        path: 'verify-email',
        name: 'VerifyEmail',
        component: () => import('@/views/access/VerifyEmail'),
      },
      {
        path: 'resend-verification',
        name: 'ResendVerification',
        component: () => import('@/views/access/ResendVerification'),
      },
      {
        path: 'forgot-password',
        name: 'ForgotPassword',
        component: () => import('@/views/access/ForgotPassword'),
      },
      {
        path: 'reset-password',
        name: 'ResetPassword',
        component: () => import('@/views/access/ResetPassword'),
      },
    ],
  },
  {
    path: '/public',
    redirect: '/access/404',
    name: 'Public',
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    children: [
      {
        path: 'public-scheduler',
        name: 'PublicScheduler',
        component: () => import('@/views/public/PublicScheduler'),
      },
    ],
  },
  {
    path: '/access',
    redirect: '/access/404',
    name: 'access',
    meta: { requiresAuth: true },
    component: {
      render() {
        return h(resolveComponent('router-view'))
      },
    },
    children: [
      {
        path: '/no-business',
        name: 'NoBusiness',
        component: () => import('@/views/access/NoBusiness.vue'),
      },
    ],
  },
  {
    path: '/',
    name: 'Home',
    component: DefaultLayout,
    redirect: '/dashboard',
    meta: { requiresAuth: true, requiresRoles: true },
    children: [
      {
        path: '/dashboard',
        name: 'Dashboard',
        component: () => import('@/views/Dashboard.vue'),
      },
      {
        path: '/business',
        name: 'Business',
        redirect: '/business/calender',
      },
      {
        path: '/business/calender',
        name: 'Calender',
        component: () => import('@/views/business/Calender.vue'),
      },
      {
        path: '/business/schedule-settings',
        name: 'ScheduleSettings',
        component: () => import('@/views/business/ScheduleSettings.vue'),
        meta: { requiresAuth: true, requiresManager: true },
      },
      {
        path: '/business/kpi',
        name: 'KPIs',
        component: () => import('@/views/business/KPI.vue'),
        meta: { requiresAuth: true, requiresOwner: true },
      },
      {
        path: '/business/employees',
        name: 'Employees',
        component: () => import('@/views/business/Employees.vue'),
        meta: { requiresAuth: true, requiresOwner: true },
      },
      {
        path: '/business/services',
        name: 'Services',
        component: () => import('@/views/business/Services.vue'),
        meta: { requiresAuth: true, requiresOwner: true },
      },
      {
        path: '/business/locations',
        name: 'Locations',
        component: () => import('@/views/business/Locations.vue'),
        meta: { requiresAuth: true, requiresOwner: true },
      },
      {
        path: '/account',
        name: 'Account',
        redirect: '/account/settings',
      },
      {
        path: '/account/settings',
        name: 'Settings',
        component: () => import('@/views/account/Settings.vue'),
      },
      {
        path: '/account/billing',
        name: 'Billing',
        component: () => import('@/views/account/Billing.vue'),
        meta: { requiresAuth: true, requiresOwner: true },
      },
      {
        path: '/help',
        name: 'Help',
        component: () => import('@/views/HelpCenter.vue'),
      },
      {
        path: '/setup',
        name: 'Setup',
        component: () => import('@/views/Setup.vue'),
        meta: {
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'Access-404',
    component: () => import('@/views/access/Page404.vue'),
    meta: { requiresAuth: false },
  },
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  scrollBehavior() {
    // always scroll to top
    return { top: 0 }
  },
})

// --- Global Route Guard ---
router.beforeEach(async (to, from, next) => {
  const requiresAuth = to.meta.requiresAuth
  const publicRoutes = [
    'Login',
    'Register',
    'ForgotPassword',
    'ResetPassword',
    'VerifyEmail',
    'ResendVerification',
    'PublicScheduler',
    'Access-404',
    'Access-500',
  ]

  // --- 1. Initialize Auth State ---
  if (!store.state.isAuthInitialized && !publicRoutes.includes(to.name)) {
    try {
      await store.dispatch('initializeAuth')
    } catch (error) {
      // If the database connection drops or network fails during auth fetch,
      // it will throw an error. Catch it and route to Login.
      // Note: Swap 'Login' for 'Access-500'
      return next({ name: 'Access-500' })
    }
  }

  // Now, get the auth state *after* initialization is complete
  let isAuthenticated = store.getters.isAuthenticated

  // --- 2. Authentication Guard ---
  if (requiresAuth && !isAuthenticated) {
    return next({ name: 'Login' })
  }

  // --- Redirect authenticated users from Login/Register ---
  if (['Login', 'Register'].includes(to.name) && isAuthenticated) {
    const isInSetupMode = store.getters.isInSetupMode

    if (isInSetupMode) {
      return next({ name: 'Setup' })
    }

    return next({ name: 'Dashboard' })
  }

  // --- 3. Setup Mode Guard ---
  if (isAuthenticated) {
    const isInSetupMode = store.getters.isInSetupMode

    if (!isInSetupMode && to.name === 'Setup') {
      return next({ name: 'Dashboard' })
    }
  }

  // --- 4. Role-Based Guards (Pass-through) ---
  const userRole = store.getters.userRole
  const requiresRoles = to.meta.requiresRoles
  const requiresManager = to.meta.requiresManager
  const requiresOwner = to.meta.requiresOwner

  if (to.name === 'NoBusiness' && userRole) {
    return next({ name: 'Dashboard' })
  }

  if (requiresRoles && !userRole) {
    return next({ name: 'NoBusiness' })
  }

  if (requiresManager && userRole !== 'manager' && userRole !== 'owner') {
    return next({ name: 'Dashboard' })
  }

  if (requiresOwner && userRole !== 'owner') {
    return next({ name: 'Dashboard' })
  }

  // --- 5. All Checks Passed ---
  next()
})

export default router
